import { useState, useEffect, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { BRAND_COLORS } from "@/lib/sections";
import { shareGuide } from "@/lib/share";
import { X, Download, Copy, Check, Link as LinkIcon, Mail, MessageCircle, Sparkles, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";

const STYLES = [
  { key: "square", label: "Quadrato" },
  { key: "rounded", label: "Arrotondato" },
  { key: "dots", label: "Punti" },
  { key: "gapped", label: "Distanziato" },
];

export function QRStudio({ property, open, onClose }) {
  const brand = property?.branding?.brand_color || "#2E7CB8";
  const [fg, setFg] = useState(brand);
  const [bg, setBg] = useState("#FFFFFF");
  const [style, setStyle] = useState("rounded");
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState("");
  const [msgLoading, setMsgLoading] = useState(false);
  const [copiedMsg, setCopiedMsg] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.get(`/properties/${property.id}/qr`, { params: { base_url: window.location.origin, fg, bg, style } })
        .then(({ data }) => setQr(data))
        .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(timer.current);
  }, [open, property?.id, fg, bg, style]);

  if (!open) return null;
  const url = qr?.url || `${window.location.origin}/g/${property.slug}`;

  const download = () => { const a = document.createElement("a"); a.href = qr.qr; a.download = `${property.slug}-qr.png`; a.click(); };
  const copyLink = () => { navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copiato"); setTimeout(() => setCopied(false), 1500); };
  const genMessage = async () => {
    setMsgLoading(true);
    try {
      const { data } = await api.post(`/properties/${property.id}/share-message`, { channel: "whatsapp" }, { params: { base_url: window.location.origin } });
      setMessage(data.message);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setMsgLoading(false); }
  };
  const copyMessage = () => { navigator.clipboard.writeText(message); setCopiedMsg(true); toast.success("Messaggio copiato"); setTimeout(() => setCopiedMsg(false), 1500); };
  const shareText = message || `Ecco la guida di ${property.name}: ${url}`;
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const email = `mailto:?subject=${encodeURIComponent(`La guida di ${property.name}`)}&body=${encodeURIComponent(shareText)}`;

  const swatches = [...new Set([brand, "#0F172A", ...BRAND_COLORS])];

  return (
    <div className="fixed inset-0 z-[60] bg-slate-50 overflow-y-auto" data-testid="qr-studio">
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b border-slate-100 h-14 flex items-center justify-between px-4 max-w-md mx-auto">
        <button onClick={onClose} className="text-slate-500" data-testid="qr-studio-close"><X className="w-5 h-5" /></button>
        <span className="font-display font-bold text-slate-900">QR Studio</span>
        <span className="w-5" />
      </header>

      <div className="max-w-md mx-auto px-5 py-5 space-y-5 pb-10">
        {/* Preview */}
        <div className="rounded-3xl bg-white border border-slate-100 p-6 flex flex-col items-center">
          <div className="rounded-3xl p-4 shadow-sm" style={{ background: bg }}>
            {loading || !qr ? <div className="w-52 h-52 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
              : <img src={qr.qr} alt="QR" className="w-52 h-52" data-testid="qr-studio-image" />}
          </div>
          <p className="mt-3 font-display font-bold text-slate-900">{property.name}</p>
          <p className="text-xs font-mono text-slate-400 break-all text-center mt-0.5">{url}</p>
        </div>

        {/* Colours */}
        <div className="rounded-3xl bg-white border border-slate-100 p-5">
          <p className="text-sm font-semibold text-slate-700 mb-3">Colore del codice</p>
          <div className="flex flex-wrap gap-2.5">
            {swatches.map((c) => (
              <button key={c} onClick={() => setFg(c)} data-testid={`qr-fg-${c}`} aria-label={`Colore ${c}`}
                className={`w-9 h-9 rounded-full border-2 transition-transform active:scale-90 ${fg === c ? "border-slate-900 scale-110" : "border-white shadow"}`} style={{ background: c }} />
            ))}
          </div>
          <p className="text-sm font-semibold text-slate-700 mt-4 mb-2">Stile</p>
          <div className="grid grid-cols-4 gap-2">
            {STYLES.map((s) => (
              <button key={s.key} onClick={() => setStyle(s.key)} data-testid={`qr-style-${s.key}`}
                className={`rounded-xl py-2 text-[11px] font-semibold border transition-colors ${style === s.key ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600"}`}>{s.label}</button>
            ))}
          </div>
        </div>

        <button onClick={download} disabled={!qr} data-testid="qr-studio-download" className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white font-semibold rounded-2xl py-3.5 disabled:opacity-50"><Download className="w-4 h-4" /> Scarica QR (PNG)</button>

        {/* Smart Share */}
        <div className="rounded-3xl bg-white border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-1"><Share2 className="w-4 h-4 text-blue-500" /><h2 className="font-display font-bold text-slate-900">Condivisione smart</h2></div>
          <p className="text-xs text-slate-400 mb-4">Invia la guida ai tuoi ospiti in un tocco.</p>

          <div className="grid grid-cols-3 gap-2">
            <button onClick={copyLink} data-testid="share-copy" className="flex flex-col items-center gap-1.5 rounded-2xl border border-slate-200 py-3 text-slate-600">{copied ? <Check className="w-5 h-5 text-emerald-500" /> : <LinkIcon className="w-5 h-5" />}<span className="text-[11px] font-semibold">Copia link</span></button>
            <a href={whatsapp} target="_blank" rel="noreferrer" data-testid="share-whatsapp" className="flex flex-col items-center gap-1.5 rounded-2xl border border-slate-200 py-3 text-slate-600"><MessageCircle className="w-5 h-5" /><span className="text-[11px] font-semibold">WhatsApp</span></a>
            <a href={email} data-testid="share-email" className="flex flex-col items-center gap-1.5 rounded-2xl border border-slate-200 py-3 text-slate-600"><Mail className="w-5 h-5" /><span className="text-[11px] font-semibold">Email</span></a>
          </div>

          <div className="mt-4 rounded-2xl bg-blue-50/60 border border-blue-100 p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-blue-900 inline-flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Messaggio con l'AI</p>
              <button onClick={genMessage} disabled={msgLoading} data-testid="share-generate-ai" className="text-xs font-bold text-blue-600">{msgLoading ? "Genero…" : (message ? "Rigenera" : "Genera")}</button>
            </div>
            {message ? (
              <>
                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} data-testid="share-message-text" className="mt-3 w-full rounded-xl border border-blue-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-blue-500 resize-none" />
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button onClick={copyMessage} className="rounded-xl border border-blue-200 bg-white text-blue-700 font-semibold py-2.5 text-sm inline-flex items-center justify-center gap-1.5">{copiedMsg ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />} Copia</button>
                  <button onClick={() => shareGuide(url, property.name)} className="rounded-xl bg-blue-600 text-white font-semibold py-2.5 text-sm inline-flex items-center justify-center gap-1.5"><Share2 className="w-4 h-4" /> Condividi</button>
                </div>
              </>
            ) : (
              <p className="text-xs text-blue-700/70 mt-2">Genera un messaggio di benvenuto pronto da inviare, basato sulle informazioni della tua struttura.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
